/**
 * Renders one assessment report in patient history (live session / mentor drawer).
 *
 * Data model (see docs/portals/mentor/assessments/patient-assessment-history.md and emr-reports-unified-client-integration.md):
 * Assessment Report is the primary object. One list entry = one report. The report contains all its runs and report-level
 * fields (header: name, age, gender; narrative: freeFormRichText, clinicalInterpretation, etc.; run-level results).
 * All runs within the report are displayed inside this single entry.
 *
 * Contract: API returns { code, message, data }. We use data["assessment-report-response"]. Each element is one
 * Assessment Report (with assessmentReport and assessmentRuns). We resolve to the report for display and use only this item's data.
 */
import React, { useState } from 'react'
import { Box, Paper, Typography, Collapse, IconButton, Button } from '@mui/material'
import ExpandMore from '@mui/icons-material/ExpandMore'
import ExpandLess from '@mui/icons-material/ExpandLess'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'

dayjs.extend(utc)
dayjs.extend(timezone)

import { GENDER_LIST_ONBOARDING } from 'src/consumer/constants'
import { getDateOfAssessmentReport, parseReportTimestamp } from 'src/mentor/components/TherapyReportMakingPage/TherapyReportMakingPageComponents'
import { getAgeFromDobWithTime } from 'src/consumer/helpers/profilePageHelper'
import {
  PMPHeaderDetails,
  PMPHeadingBanner,
} from 'src/mentor/components/PrescriptionMakingPage/PrescriptionMakingPageStyles'
import AssessmentReportBody from 'src/mentor/components/AssessmentReport/viewComponents/AssessmentReportBody'

function getMinMax(rangeItem) {
  const min = rangeItem?.min ?? rangeItem?.from ?? 0
  const max = rangeItem?.max ?? rangeItem?.to ?? 0
  return { min, max }
}

/** Pick the scoring range whose min/max contains userScore; use its title and interpretation (patient-assessment-history §2). */
function getScoringRangeForScore(resultItem) {
  const rangeList = resultItem?.scoringRangeList ?? []
  const match = rangeList.find((r) => {
    const { min, max } = getMinMax(r)
    const score = resultItem?.userScore ?? 0
    return min <= score && score <= max
  })
  return match
}

/**
 * API returns { code, message, data }. assessment-report-response is nested under data (data["assessment-report-response"]).
 * @param {Object} report - One element from data["assessment-report-response"] (one report with all runs). Has nested
 *   "assessmentReport" (id, patientName, freeFormRichText, narrative sections, assessmentRunIds) and "assessmentRuns" (all runs for this report).
 * @param {Function} onViewAnswers
 */
export default function AssessmentReportHistoryView({ report, onViewAnswers }) {
  const assessmentReport = report?.assessmentReport
  const assessmentRuns = report?.assessmentRuns ?? report?.assessmentReport?.assessmentRuns ?? []
  // AssessmentReport is primary; report is an AssessmentResponse (no top-level id). Identity is assessmentReport.id.
  const itemId = report?.assessmentReport?.id
  const runIds = assessmentRuns.map((r) => r?.id ?? r?.assessmentRunId).filter(Boolean)
  const reportRunIds = assessmentReport?.assessmentRunIds ?? []
  const runsBelongToReport =
    reportRunIds.length > 0 && runIds.every((id) => reportRunIds.includes(id))
  const useNested =
    assessmentReport &&
    assessmentReport.id === itemId &&
    (itemId != null || runsBelongToReport)
  const effectiveAssessmentReport = useNested ? assessmentReport : report
  const [expandedArticleId, setExpandedArticleId] = useState(null)

  // Sort runs by completedAt (latest first). Doc §2: show latest by completedAt; we show all runs (latest first).
  const sortedRuns =
    assessmentRuns.length > 0
      ? [...assessmentRuns].sort((a, b) => {
          const ma = parseReportTimestamp(a?.completedAt)
          const mb = parseReportTimestamp(b?.completedAt)
          return (mb?.valueOf() ?? 0) - (ma?.valueOf() ?? 0)
        })
      : []
  const latestRun = sortedRuns[0]
  const assessmentDate = getDateOfAssessmentReport(effectiveAssessmentReport, latestRun)
  const assessmentDateWithTime = getDateOfAssessmentReport(effectiveAssessmentReport, latestRun, true)

  if (!effectiveAssessmentReport && assessmentRuns.length === 0) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="text.secondary">No assessment data to display.</Typography>
      </Box>
    )
  }

  /** Run id for API (doc §3/§7: id and assessmentRunId are same). */
  const getRunId = (run) => run?.id ?? run?.assessmentRunId

  /** Render domain results + articles for a single run (one assessment scale). Doc §2: domainResults, scoring range title + interpretation, articles embedded. */
  const renderRunContent = (run, runIndex) => {
    const domainResults = run?.domainResults ?? []
    const articles = run?.articles ?? []
    const runId = getRunId(run)
    const runTitle =
      run?.clinicalAssessmentTitle ?? run?.assessmentTitle ?? run?.psychAssessmentTitle ?? `Assessment ${runIndex + 1}`
    return (
      <Box key={runId ?? runIndex} sx={{ pt: runIndex === 0 ? 2 : 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
          {sortedRuns.length > 1 && (
            <Typography variant="subtitle1" fontWeight={700} color="primary" sx={{ mb: 0.5 }}>
              {runTitle}
            </Typography>
          )}
          {onViewAnswers && runId && (
            <Button
              size="small"
              variant="outlined"
              onClick={() => onViewAnswers(runId)}
              sx={{ fontSize: '0.75rem', textTransform: 'none' }}
            >
              View answers
            </Button>
          )}
        </Box>
        {domainResults.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No domain results for this run.
          </Typography>
        ) : (
          domainResults.map((resultItem, idx) => {
            const scoringRange = getScoringRangeForScore(resultItem)
            const severityLabel = scoringRange?.title ?? null
            const interpretation = scoringRange?.interpretation ?? null
            return (
              <Paper
                key={resultItem?.domainId ?? idx}
                elevation={0}
                variant="outlined"
                sx={{ p: 1.5, borderRadius: 2, borderColor: 'divider' }}
              >
                <Typography variant="subtitle2" fontWeight={600} color="text.primary">
                  {resultItem?.domainName ?? 'Domain'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Score: {resultItem?.userScore ?? 0} / {resultItem?.totalScore ?? 0}
                  {severityLabel ? ` · ${severityLabel}` : ''}
                </Typography>
                {interpretation && (
                  <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary', fontStyle: 'italic' }}>
                    {interpretation}
                  </Typography>
                )}
              </Paper>
            )
          })
        )}
        {articles.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
              Related articles
            </Typography>
            {articles.map((art, artIdx) => {
              const artId = `${runId ?? runIndex}-${art?.articleId ?? art?.domainId ?? artIdx}`
              const isExpanded = expandedArticleId === artId
              const hasContent = art?.article?.content ?? art?.content
              const htmlContent =
                (typeof art?.article?.content === 'string' && art.article.content) ||
                (typeof art?.content === 'string' && art.content) ||
                ''
              const htmlProp = { __html: htmlContent }
              return (
                <Paper
                  key={artId}
                  elevation={0}
                  variant="outlined"
                  sx={{ mb: 1, borderRadius: 2, borderColor: 'divider', overflow: 'hidden' }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      px: 1.5,
                      py: 1,
                      cursor: hasContent ? 'pointer' : 'default',
                    }}
                    onClick={() => (hasContent ? setExpandedArticleId(isExpanded ? null : artId) : undefined)}
                  >
                    <Typography variant="body2" fontWeight={500}>
                      {art?.articleTitle ?? art?.article?.title ?? art?.domainName ?? 'Article'}
                    </Typography>
                    {hasContent && (
                      <IconButton size="small" disableRipple>
                        {isExpanded ? <ExpandLess /> : <ExpandMore />}
                      </IconButton>
                    )}
                  </Box>
                  {hasContent && (
                    <Collapse in={isExpanded}>
                      <Box sx={{ px: 1.5, pb: 1.5, fontSize: '0.875rem', color: 'text.secondary' }}>
                        <div style={{ marginBottom: 0 }} dangerouslySetInnerHTML={htmlProp} />
                      </Box>
                    </Collapse>
                  )}
                </Paper>
              )
            })}
          </Box>
        )}
      </Box>
    )
  }

  // After expansion we have one run per block. Always use this block's run scale for the banner so PHQ-9 shows "Patient Health Questionnaire...", GAD-7 shows "GAD-7...", etc., not the report-level title.
  const runForBanner = assessmentRuns?.[0]
  const runScaleTitle = runForBanner?.clinicalAssessmentTitle ?? runForBanner?.psychAssessmentTitle ?? runForBanner?.assessmentTitle
  const baseTitle = (runScaleTitle && String(runScaleTitle).trim()) || effectiveAssessmentReport?.title || 'Assessment Report'
  const reportTitle =
    baseTitle && !/assessment/i.test(baseTitle) ? `${baseTitle} Assessment` : baseTitle

  // freeFormRichText is optional; show narrative sections when present (AssessmentReportBody renders only sections with content).
  const reportDataForSections = effectiveAssessmentReport
    ? {
        ...effectiveAssessmentReport,
        freeFormRichText: effectiveAssessmentReport?.freeFormRichText ?? effectiveAssessmentReport?.content ?? '',
      }
    : null
  // EMR section order/titles from API when present (same as Screening/Therapy/Consultation history).
  const orderedSectionsForPrint = report?.emrSections ?? report?.assessmentReport?.emrSections ?? null

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
      <Box sx={{ background: '#FFF' }}>
        <PMPHeadingBanner noColor style={{ position: 'relative', zIndex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <span>{reportTitle}</span>
          </div>
        </PMPHeadingBanner>
        <PMPHeaderDetails
          noColor
          style={{
            position: 'relative',
            zIndex: 1,
            padding: '16px 24px',
            backgroundColor: '#f8fafc',
            borderTop: '1px solid #e2e8f0',
            borderBottom: '1px solid #e2e8f0',
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '16px 24px',
            alignItems: 'start',
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
            <Box sx={{ fontSize: '14px', color: '#64748b', fontWeight: 500 }}>
              Patient Name:{' '}
              <Box component="span" sx={{ textTransform: 'capitalize', color: '#1e293b', fontWeight: 600 }}>
                {effectiveAssessmentReport?.patientName ?? '—'}{' '}
                {effectiveAssessmentReport?.patientNumber ? `(${effectiveAssessmentReport.patientNumber})` : ''}
              </Box>
            </Box>
            <Box sx={{ fontSize: '14px', color: '#64748b', fontWeight: 500 }}>
              Age/Gender:{' '}
              <Box component="span" sx={{ color: '#1e293b', fontWeight: 600 }}>
                {effectiveAssessmentReport?.patientDateOfBirth
                  ? getAgeFromDobWithTime(effectiveAssessmentReport.patientDateOfBirth)
                  : effectiveAssessmentReport?.patientAge ?? '—'}
                /
                {effectiveAssessmentReport?.patientGender
                  ? GENDER_LIST_ONBOARDING?.find(
                      (item) => item.value === effectiveAssessmentReport.patientGender,
                    )?.label ?? effectiveAssessmentReport.patientGender
                  : '—'}
              </Box>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0, textAlign: 'right' }}>
            {(assessmentDateWithTime ?? assessmentDate) && (
              <Box sx={{ fontSize: '14px', color: '#64748b', fontWeight: 500 }}>
                Date & time:{' '}
                <Box component="span" sx={{ color: '#1e293b', fontWeight: 600 }}>{assessmentDateWithTime ?? assessmentDate}</Box>
              </Box>
            )}
            {effectiveAssessmentReport?.mentorName && (
              <Box sx={{ fontSize: '14px', color: '#64748b', fontWeight: 500 }}>
                Practitioner:{' '}
                <Box component="span" sx={{ color: '#1e293b', fontWeight: 600 }}>{effectiveAssessmentReport.mentorName}</Box>
              </Box>
            )}
          </Box>
        </PMPHeaderDetails>
      </Box>

      {sortedRuns.map((run, runIndex) => renderRunContent(run, runIndex))}

      {/* Narrative sections (EMR order) from this assessmentResponse's assessmentReport only. Key by report id so React never reuses another report's body. */}
      {reportDataForSections && (
        <Box key={effectiveAssessmentReport?.id ?? 'narrative'} sx={{ px: 2, pt: 2, pb: 2 }}>
          <AssessmentReportBody
            reportData={reportDataForSections}
            orderedSectionsForPrint={orderedSectionsForPrint}
          />
        </Box>
      )}
    </Box>
  )
}
